import { useState } from 'react'

import { IconName, library } from '@fortawesome/fontawesome-svg-core'
import {
  faAward,
  faBed,
  faBook,
  faBriefcase,
  faCloudSun,
  faComment,
  faGlobe,
  faMapMarkerAlt,
  faMedal,
  faMegaphone,
  faPaperPlane,
  faPlaceOfWorship,
  faRoute,
  faRuler,
  faSackDollar,
  faShieldAlt,
  faVoteYea,
} from '@fortawesome/pro-regular-svg-icons'
import {
  faAward as solidFaAward,
  faBed as solidFaBed,
  faBook as solidFaBook,
  faBriefcase as solidFaBriefcase,
  faCloudSun as solidFaCloudSun,
  faComment as solidFaComment,
  faGlobe as solidFaGlobe,
  faMapMarkerAlt as solidFaMapMarkerAlt,
  faMedal as solidFaMedal,
  faMegaphone as solidFaMegaphone,
  faPaperPlane as solidFaPaperPlane,
  faPlaceOfWorship as solidFaPlaceOfWorship,
  faRoute as solidFaRoute,
  faRuler as solidFaRuler,
  faSackDollar as solidFaSackDollar,
  faShieldAlt as solidFaShieldAlt,
  faVoteYea as solidFaVoteYea,
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Chip, TextField, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { ConfirmationDialog } from '@scoir/modals'

import { MicroSurveyTag } from 'store/microsurvey'
import { useMicroSurveyUnfollowTags } from 'store/microsurvey/hooks'

import { submitSurveyResults } from './use-survey'

library.add(
  faSackDollar,
  solidFaSackDollar,
  faBook,
  solidFaBook,
  faVoteYea,
  solidFaVoteYea,
  faBriefcase,
  solidFaBriefcase,
  faRoute,
  solidFaRoute,
  faMapMarkerAlt,
  solidFaMapMarkerAlt,
  faCloudSun,
  solidFaCloudSun,
  faMegaphone,
  solidFaMegaphone,
  faGlobe,
  solidFaGlobe,
  faRuler,
  solidFaRuler,
  faShieldAlt,
  solidFaShieldAlt,
  faBed,
  solidFaBed,
  faMedal,
  solidFaMedal,
  faPlaceOfWorship,
  solidFaPlaceOfWorship,
  faAward,
  solidFaAward,
  faPaperPlane,
  solidFaPaperPlane,
  faComment,
  solidFaComment,
)

const useStyles = makeStyles((theme) => ({
  subtitle: {
    marginBottom: theme.spacing(2),
  },
  caption: {
    marginTop: theme.spacing(2),
    display: 'block',
  },
  tags: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  selectedChip: {
    backgroundColor: '#D9EEF8',
    '&.MuiChip-clickable.MuiChip-outlinedPrimary:focus': {
      backgroundColor: '#D9EEF8',
    },
  },
  chipIcon: {
    paddingLeft: '6px',
    marginLeft: '4px',
    // override chip icon for changing from regular to solid
    width: '14px!important',
    boxSizing: 'content-box',
  },
  chipLabel: {
    textOverflow: 'initial',
    color: theme.palette.common.black,
  },
}))

const ADD_COLLEGE_ACTION = {
  UNFOLLOW: 'unfollow',
  FOLLOW: 'follow',
  REJECT: 'reject',
  SUGGEST_REJECT: 'suggest_reject',
  ENROLLMENT: 'enrollment',
}

type SimplifiedProspect = {
  CollegeName: string
  CollegeScid: number
  Suggestions?: {
    SuggestedById: string
  }[]
}

type CollegeSwitchSurveyProps = {
  type: 'unfollow' | 'follow' | 'reject' | 'enrollment'
  referrer: string
  show: boolean
  prospect?: SimplifiedProspect
  onClose: () => void
  onSubmit: (prospect?: { CollegeName: string; CollegeScid: number }) => void
}

const CollegeSwitchSurvey = ({ type, referrer, show, prospect, onClose, onSubmit }: CollegeSwitchSurveyProps) => {
  const classes = useStyles()
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const charLimit = 240
  const [otherReason, setOtherReason] = useState<string>()
  const [otherError, setOtherError] = useState<boolean>(false)
  // Using the same hook for both follow and unfollow to get identical tags
  const tags = useMicroSurveyUnfollowTags()

  const getHeaderText = () => {
    switch (type) {
      case ADD_COLLEGE_ACTION.UNFOLLOW:
      case ADD_COLLEGE_ACTION.SUGGEST_REJECT:
        return `${prospect?.CollegeName} isn't a fit?`
      case ADD_COLLEGE_ACTION.FOLLOW:
        return `${prospect?.CollegeName} is now on your list!`
      case ADD_COLLEGE_ACTION.REJECT:
        return `Not interested in ${prospect?.CollegeName}?`
      case ADD_COLLEGE_ACTION.ENROLLMENT:
        return `Congrats on enrolling in ${prospect?.CollegeName}!`
      default:
        return ''
    }
  }

  const getSubTitle = () => {
    switch (type) {
      case ADD_COLLEGE_ACTION.UNFOLLOW:
      case ADD_COLLEGE_ACTION.SUGGEST_REJECT:
        return `🧐 Help us improve your experience. What don’t you like?`
      case ADD_COLLEGE_ACTION.FOLLOW:
        return `🎉 Help us improve your experience. What made you follow?`
      case ADD_COLLEGE_ACTION.REJECT:
        return `You won’t receive any other messages from this college and it will be moved to your Not Interested list. Share why you’re not interested.`
      case ADD_COLLEGE_ACTION.ENROLLMENT:
        return `🎉 Help us improve Scoir. What made you enroll?`
      default:
        return ''
    }
  }
  const onToggle = (tag: MicroSurveyTag) => {
    if (selectedTags.includes(tag?.Id)) {
      setSelectedTags(selectedTags.filter((t) => t !== tag.Id))
    } else {
      setSelectedTags([...selectedTags, tag.Id])
    }
  }

  const submitSurvey = () => {
    let finalType = type
    let suggestedBy = undefined
    if (type === ADD_COLLEGE_ACTION.SUGGEST_REJECT) {
      finalType = ADD_COLLEGE_ACTION.REJECT as 'reject'
    }
    if (prospect?.Suggestions) {
      suggestedBy = prospect.Suggestions.map((s) => s.SuggestedById)
    }
    submitSurveyResults({
      collegeSCID: prospect!.CollegeScid,
      tags: selectedTags,
      surveyType: finalType,
      freeFormReason: otherReason,
      referrer,
      suggestedBy,
    })
    setSelectedTags([])
    onSubmit(prospect)
    onClose()
  }

  const getCancellationText = () => {
    switch (type) {
      case ADD_COLLEGE_ACTION.UNFOLLOW:
        return 'Keep Following'
      case ADD_COLLEGE_ACTION.FOLLOW:
      case ADD_COLLEGE_ACTION.ENROLLMENT:
        return 'Dismiss'
      case ADD_COLLEGE_ACTION.REJECT:
      case ADD_COLLEGE_ACTION.SUGGEST_REJECT:
        return 'Cancel'
      default:
        return ''
    }
  }

  const getConfirmationText = () => {
    switch (type) {
      case ADD_COLLEGE_ACTION.UNFOLLOW:
        return 'Unfollow'
      case ADD_COLLEGE_ACTION.FOLLOW:
      case ADD_COLLEGE_ACTION.ENROLLMENT:
        return 'Save'
      case ADD_COLLEGE_ACTION.REJECT:
      case ADD_COLLEGE_ACTION.SUGGEST_REJECT:
        return 'Mark as Not Interested'
      default:
        return ''
    }
  }

  return (
    <ConfirmationDialog
      open={show}
      headerText={getHeaderText()}
      onClose={() => {
        setSelectedTags([])
        onClose()
      }}
      onConfirm={submitSurvey}
      cancellationText={getCancellationText()}
      confirmationText={getConfirmationText()}
    >
      <Typography
        variant="subtitle1"
        className={classes.subtitle}
      >
        {getSubTitle()}
      </Typography>
      <Box className={classes.tags}>
        {tags.map((tag: MicroSurveyTag) => {
          const { Id, Name, Icon } = tag
          const isSelected = selectedTags.includes(Id)
          return (
            <Chip
              key={Id}
              classes={{ root: isSelected ? classes.selectedChip : undefined, label: classes.chipLabel }}
              label={Name}
              onClick={() => onToggle(tag)}
              color="primary"
              variant="outlined"
              icon={
                <FontAwesomeIcon
                  className={classes.chipIcon}
                  icon={[isSelected ? 'fas' : 'far', Icon as IconName]}
                />
              }
            />
          )
        })}
      </Box>
      {type === ADD_COLLEGE_ACTION.ENROLLMENT && (
        <Box my={2}>
          <TextField
            fullWidth
            multiline
            label="Share another reason"
            variant="outlined"
            helperText={
              otherReason && otherReason?.length > 0
                ? `${charLimit - otherReason?.length} characters left`
                : `Enter up to ${charLimit} characters`
            }
            error={otherError}
            onChange={(e) => {
              setOtherReason(e.target.value)
              if (e.target.value.length > charLimit) {
                setOtherError(true)
              } else if (otherError) {
                setOtherError(false)
              }
            }}
          />
        </Box>
      )}
      <Typography
        variant="caption"
        className={classes.caption}
      >
        Your feedback helps us understand student preferences and will not be shared with colleges.
      </Typography>
    </ConfirmationDialog>
  )
}

export default CollegeSwitchSurvey
